import { Box, Flex } from '@chakra-ui/react'
import { omit } from 'lodash-es'
import { useRouter } from 'next/router'
import { useState } from 'react'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'

import Select from '@/components/UI/Select'
import Tippy from '@/components/UI/Tippy'

import { DEFAULT_PAGE_NUMBER_QUERY_KEY, DEFAULT_QUERY_KEY_SORT } from './constants'
import { parseQueryValue, sanitizeQueryValue } from './helpers'
import type { Option, SortWidgetProps } from './types'

interface Props<T> {
  sorts: Record<string, T> | { label: string; value: T }[]
  defaultSortKey?: string
  queryKey?: string
  sideEffectQueryKeys?: string[]
  useValueInQuery?: boolean
  passthroughUnknown?: boolean
}

export default function useSortWidgetV2<T>({
  sorts: sortsOriginal,
  defaultSortKey,
  queryKey = DEFAULT_QUERY_KEY_SORT,
  sideEffectQueryKeys,
  useValueInQuery,
  passthroughUnknown,
}: Props<T>) {
  const sorts = Array.isArray(sortsOriginal)
    ? Object.fromEntries(sortsOriginal.map(item => [item.label, item.value]))
    : sortsOriginal

  const queryValue = parseQueryValue(queryKey, useRouter().query)
  // value can be any arbitrary Javascript value, inputValue is url-safe key
  // for the specific option
  const options: Option[] = Object.keys(sorts).map(label => {
    const value = useValueInQuery === true ? sorts[label] : label
    return {
      label,
      inputValue: sanitizeQueryValue(typeof value === 'string' ? value : label),
      value,
    }
  })

  const optionMatchingQuery = options.find(item => item.inputValue === queryValue)
  const optionDefault = options.find(item => item.value === defaultSortKey)
  const optionFallback = options[0]

  let value: T | null = null
  if (optionMatchingQuery != null) {
    value = sorts[optionMatchingQuery.label]
  } else if (queryValue != null && passthroughUnknown === true) {
    value = queryValue as T
  } else if (optionDefault != null) {
    value = sorts[optionDefault.label]
  } else {
    value = sorts[optionFallback.label]
  }

  function Component({
    zIndex = 'auto',
    width = '15rem',
    text = 'Sort by',
    direction = 'row',
    fontWeight = 'bold',
    menuPortalTargetRef,
    tooltipText,
  }: Omit<SortWidgetProps<T>, 'sorts' | 'defaultSortKey' | 'queryKey'> & {
    direction?: 'row' | 'column'
    fontWeight?: string
  }) {
    const router = useRouter()

    const [optionOptimistic, setOptionOptimistic] = useState<Option | null>(null)
    const option = optionOptimistic ?? optionMatchingQuery ?? optionDefault ?? optionFallback

    const currentPageId = router.query[DEFAULT_PAGE_NUMBER_QUERY_KEY]

    const [handleSortChange] = useAPIWrapper(
      async (newOption?: Option | null) => {
        if (newOption == null) {
          return
        }
        // Optimistic update
        setOptionOptimistic(newOption)
        const pageReset = currentPageId != null ? { [DEFAULT_PAGE_NUMBER_QUERY_KEY]: 1 } : {}

        await router.replace({
          query: {
            ...omit(router.query, [queryKey, ...(sideEffectQueryKeys ?? [])]),
            ...(newOption.inputValue == null ? {} : { [queryKey]: newOption.inputValue }),
            ...pageReset,
          },
        })

        // Revert optimistic update to let the query stuff take precedence
        setOptionOptimistic(null)
      },
      [currentPageId, router],
      'Process Sort Value change'
    )

    return (
      <Flex
        direction={direction}
        align={direction === 'row' ? 'center' : 'left'}
        justify="space-between"
        gap="2px"
      >
        <Tippy content={tooltipText} disabled={tooltipText == null}>
          {text !== '' ? (
            <Box fontWeight={fontWeight} mr={2} flexShrink={0}>
              {text}
            </Box>
          ) : null}
        </Tippy>
        <Box width={width} zIndex={zIndex}>
          <Select<Option>
            instanceId={queryKey}
            name={queryKey}
            options={options}
            value={option}
            onChange={handleSortChange}
            isSearchable
            isClearable={false}
            components={{
              IndicatorSeparator: () => null,
            }}
            menuPortalTarget={menuPortalTargetRef?.current}
          />
        </Box>
      </Flex>
    )
  }

  return { value, Component }
}
